import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, X, Send, Mail, RotateCcw } from "lucide-react";
import faqData, { fallbackAnswer, findBestMatch } from "./faqData";

const WELCOME_MESSAGE = {
  id: "welcome",
  from: "bot",
  text:
    "Bonjour 👋 Je suis l'assistant virtuel de la CCCA. Posez-moi une question ou choisissez un sujet ci-dessous.",
};

let messageIdCounter = 0;
const nextId = () => `msg-${Date.now()}-${messageIdCounter++}`;

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [inputValue, setInputValue] = useState("");
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isOpen]);

  const pushMessage = (message) => {
    setMessages((prev) => [...prev, { id: nextId(), ...message }]);
  };

  const answerWith = (item) => {
    pushMessage({
      from: "bot",
      text: item.answer,
      link: item.link,
      email: item.email,
      isFallback: item.isFallback,
    });
  };

  const handleGoHome = () => {
    setMessages([WELCOME_MESSAGE]);
  };

  const handleSuggestedQuestion = (item) => {
    pushMessage({ from: "user", text: item.question });
    setTimeout(() => answerWith(item), 300);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const text = inputValue.trim();
    if (!text) return;

    pushMessage({ from: "user", text });
    setInputValue("");

    setTimeout(() => {
      const match = findBestMatch(text);
      answerWith(match || fallbackAnswer);
    }, 300);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={isOpen ? "Fermer l'assistant CCCA" : "Ouvrir l'assistant CCCA"}
        className="fixed z-[1005] bottom-24 right-10 max-md:bottom-20 max-md:right-5 flex items-center justify-center w-[55px] h-[55px] rounded-full bg-secondary text-white shadow-lg hover:bg-opacity-90 transition-all"
      >
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {isOpen && (
        <div className="fixed z-[1005] bottom-[8.5rem] right-10 max-md:bottom-[7.5rem] max-md:right-4 max-md:left-4 w-[92vw] max-w-[360px] h-[70vh] max-h-[500px] bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden">
          <div className="bg-primary text-white px-4 py-3 flex items-center justify-between">
            <div>
              <p className="font-semibold leading-tight">Assistant CCCA</p>
              <p className="text-xs text-white/70 leading-tight">Réponses instantanées</p>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              aria-label="Fermer l'assistant CCCA"
              className="text-white/80 hover:text-white"
            >
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-gray-50">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex flex-col ${message.from === "user" ? "items-end" : "items-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                    message.from === "user"
                      ? "bg-primary text-white rounded-br-sm"
                      : "bg-white text-gray-800 border border-gray-200 rounded-bl-sm"
                  }`}
                >
                  {message.text}
                </div>
                {message.link && (
                  <Link
                    to={message.link.to}
                    onClick={() => setIsOpen(false)}
                    className="mt-1 text-xs font-semibold text-secondary hover:underline"
                  >
                    {message.link.label} →
                  </Link>
                )}
                {message.email && (
                  <a
                    href={`mailto:${message.email}`}
                    className="mt-1 flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                  >
                    <Mail size={12} /> {message.email}
                  </a>
                )}
                {message.isFallback && (
                  <button
                    type="button"
                    onClick={handleGoHome}
                    className="mt-2 flex items-center gap-1 text-xs font-semibold text-gray-600 border border-gray-300 rounded-full px-3 py-1 hover:bg-gray-100"
                  >
                    <RotateCcw size={12} /> Retour à l'accueil
                  </button>
                )}
              </div>
            ))}

            {messages.length <= 1 && (
              <div className="flex flex-col gap-2 pt-1">
                {faqData.slice(0, 5).map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleSuggestedQuestion(item)}
                    className="text-left text-sm px-3 py-2 rounded-lg border border-primary/20 text-primary bg-white hover:bg-primary/5 transition-colors"
                  >
                    {item.question}
                  </button>
                ))}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleSubmit} className="flex items-center gap-2 p-3 border-t border-gray-200 bg-white">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Posez votre question..."
              className="flex-1 text-sm px-3 py-2 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <button
              type="submit"
              aria-label="Envoyer"
              className="flex items-center justify-center w-9 h-9 rounded-full bg-secondary text-white shrink-0 disabled:opacity-40"
              disabled={!inputValue.trim()}
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
